import $r from 'sitewide-js/core_pre_webpack/routes';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import { ResearchInterest } from 'js/liswp/types';

export interface VideoOwner {
  id: number;
  displayName: string;
  photo: string;
  url: string;
}

export interface MuseVideo {
  id: string;
  fid: string;
  svid: string;
  views: number;
  duration: number;
  thumbnail_url: string;
}

export interface VideoCategory {
  id: string;
  name: string;
  isOtherCategory: boolean;
}

export type VideoCategories = { [key: string]: VideoCategory };

export type VideoVisibility =
  | 'open'
  | 'deleted'
  | 'deleted_by_admin'
  | 'hidden_by_admin'
  | 'undistributable';

export type VideoProcessingStatus =
  | 'processing_complete'
  | 'processing_in_progress'
  | 'processing_failed';

export interface Video {
  id: string;
  title: string;
  description: string;
  createdAt: string;
  url: string;
  owner: VideoOwner;
  researchInterests: Array<ResearchInterest>;
  processingStatus: VideoProcessingStatus;
  museVideo?: MuseVideo;
  videoCategory: VideoCategory | null;
  otherVideoCategoryDescription: string | null;
  visibility: VideoVisibility;
  hlsPlaylistUrl?: string;
  dashManifestUrl?: string;
  thumbnailUrl?: string;
  viewCount: number;
}

export interface VideoParams {
  title: string;
  description: string;
  videoCategoryId: string;
  otherCategoryDescription?: string | null;
  researchInterestIds: Array<number>;
  signedBlobId?: string | null;
  smsAuthFailed?: boolean | null;
}

export function createVideo(params: VideoParams): Promise<Response> {
  const route = $r.api_v0_videos_path({ subdomain_param: 'api' });
  return submitVideo(route, 'POST', params);
}

export function updateVideo(
  id: string,
  params: VideoParams,
): Promise<Response> {
  const route = $r.api_v0_video_path({ id: id, subdomain_param: 'api' });
  return submitVideo(route, 'PUT', params);
}

export function deleteVideo(id: string): Promise<Response> {
  return fetchWithCsrf(
    $r.api_v0_video_path({ id: id, subdomain_param: 'api' }),
    {
      method: 'DELETE',
    },
  );
}

function submitVideo(
  route: string,
  method: string,
  params: VideoParams,
): Promise<Response> {
  const {
    title,
    description,
    videoCategoryId,
    otherCategoryDescription,
    researchInterestIds,
    signedBlobId,
    smsAuthFailed,
  } = params;

  const form = new FormData();
  form.append('title', title);
  form.append('description', description);
  form.append('video_category_id', videoCategoryId);
  researchInterestIds.forEach((id) => {
    form.append('research_interest_ids[]', id.toString());
  });

  if (otherCategoryDescription) {
    form.append('other_category_description', otherCategoryDescription);
  }

  if (signedBlobId) {
    form.append('signed_blob_id', signedBlobId);
  }

  if (smsAuthFailed) {
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'boolean' is not assignable to pa... Remove this comment to see the full error message
    form.append('sms_auth_failed', smsAuthFailed);
  }

  return fetchWithCsrf(route, {
    method: method,
    body: form,
  });
}

export const getDurationString = (duration: number): string => {
  const seconds = Math.floor(duration % 60);
  const minutes = Math.floor(duration / 60) % 60;
  const hours = Math.floor(duration / 3600);
  return [
    hours && `${hours}`.padStart(2, '0'),
    `${minutes}`.padStart(2, '0'),
    `${seconds}`.padStart(2, '0'),
  ]
    .filter(Boolean)
    .join(':');
};
